import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getPlanConfig } from '../../utils/membershipUtils';
import VerificationBadge from '../../components/VerificationBadge/VerificationBadge';
import { API_BASE_URL, SOCKET_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import './ProfessionalProfilePage.css';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const ProfessionalProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [professionalData, setProfessionalData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showMobile, setShowMobile] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/specialists/${id}`);
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        if (res.ok) {
          const data = await res.json();
          const s = data.specialist;
          setProfessionalData({
            id: s.id,
            avatar: getImageUrl(s.avatar) || '/uploads/builder-images/848d07b91856af19f3110e8d9dbfb236bec2b554_w176.jpg',
            title: s.title || 'Specialist',
            name: s.name,
            location: s.country || 'N/A',
            projectCount: (data.projects || []).length,
            membershipPlan: s.membershipPlan || 'basic',
            isVerified: s.isEmailVerified || s.isVerified || false,
            verificationLevel: s.verificationLevel || null,
            rating: '4.5',
            category: s.category || 'N/A',
            expertise: s.expertise || 'N/A',
            interestedLocations: s.interestedLocations || 'N/A',
            experience: s.experience || 'N/A',
            coverImage: getImageUrl(s.coverImage || s.avatar) || '/uploads/builder-images/b93e1ce7b82724d8f6137167ac3cbbb8a6ea3b58_w1798.png',
            phone: s.phone || '',
          });
          const mappedProjects = (data.projects || []).map(p => {
            let img = '/uploads/builder-images/bdf28fac139b7aefbdcafb52da7f3cb6265a669a_w875.png';
            try {
              const images = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
              if (Array.isArray(images) && images.length > 0) img = getImageUrl(images[0]);
              else if (p.image) img = getImageUrl(p.image);
            } catch (_) {
              if (p.image) img = getImageUrl(p.image);
            }
            return {
              id: p.id,
              type: p.projectType || p.category || 'Project',
              title: p.title || 'Untitled Project',
              description: p.description || p.title || '',
              image: img,
            };
          });
          setProjects(mappedProjects);
        }
      } catch (error) {
        console.error('Error fetching specialist profile:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="professional-profile-page">
        <Header />
        <main className="professional-profile-content" style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}>
          <p style={{fontSize:'18px',color:'#666'}}>Loading professional profile...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !professionalData) {
    return (
      <div className="professional-profile-page">
        <Header />
        <main className="professional-profile-content" style={{display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',minHeight:'60vh',gap:'16px'}}>
          <p style={{fontSize:'18px',color:'#666'}}>This professional profile is not available.</p>
          <button onClick={() => navigate('/professionals')} style={{padding:'10px 24px',background:'#1F396C',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'14px'}}>Browse Professionals</button>
        </main>
        <Footer />
      </div>
    );
  }

  const planConfig = getPlanConfig(professionalData.membershipPlan);

  return (
    <div className="professional-profile-page">
      <Header />
      
      <main className="professional-profile-content">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Link to="/specialists" className="breadcrumb-link">Professional</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="breadcrumb-current">Professional Profile</span>
        </div>

        <div className="profile-wrapper">
          {/* Profile Header Section */}
          <div className="profile-header-section">
            <div className="profile-info-container">
              {/* Avatar and Project Count */}
              <div className="profile-top-row">
                <div className="profile-avatar">
                  <img 
                    src={professionalData.avatar} 
                    alt={professionalData.name}
                    className="avatar-image"
                  />
                </div>
                <div className="project-count">
                  <span className="count-number">{professionalData.projectCount}</span>
                  <span className="count-label">Project</span>
                </div>
              </div>

              {/* Professional Title and Details */}
              <div className="profile-details">
                <h1 className="profile-title">{professionalData.title}</h1>
                <div className="profile-name-location">
                  <span className="profile-name">{professionalData.name}</span>
                  <span className="separator"> |  </span>
                  <span className="profile-location">{professionalData.location}</span>
                </div>

                {/* Badges Row */}
                <div className="profile-badges">
                  <div className="membership-badge">
                    <svg className="membership-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#F5A623"/>
                      <circle cx="12" cy="12" r="7" fill="#F7C948"/>
                      <ellipse cx="12" cy="12" rx="4" ry="7" stroke="#D4920B" strokeWidth="0.8" fill="none"/>
                      <line x1="5" y1="12" x2="19" y2="12" stroke="#D4920B" strokeWidth="0.8"/>
                      <line x1="12" y1="5" x2="12" y2="19" stroke="#D4920B" strokeWidth="0.8"/>
                    </svg>
                    <span className="membership-text">{planConfig.label}</span>
                  </div>
                  {(professionalData.verificationLevel || professionalData.isVerified) && (
                    <div className="inline-verified-badge">
                      <svg className="inline-verified-icon" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" fill="#29ABE2"/>
                        <path d="M7 12.5L10.5 16L17 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="inline-verified-text">Verified</span>
                    </div>
                  )}
                  <div className="rating-badge">
                    <span className="rating-value">{professionalData.rating}</span>
                    <span className="rating-label">Reviews</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="profile-actions">
                <button className="btn-primary" onClick={() => {
                  if (!isAuthenticated) {
                    setShowLoginAlert(true);
                    setTimeout(() => setShowLoginAlert(false), 3000);
                    return;
                  }
                  setShowMobile(!showMobile);
                }}>
                  {showMobile && professionalData.phone ? professionalData.phone : 'View Mobile'}
                </button>
                <button className="btn-secondary" onClick={() => {
                  if (!isAuthenticated) {
                    setShowLoginAlert(true);
                    setTimeout(() => setShowLoginAlert(false), 3000);
                    return;
                  }
                  setShowContactModal(true);
                  setContactSuccess(false);
                  setContactMessage('');
                }}>Chat</button>
              </div>
              {showLoginAlert && (
                <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '6px', padding: '6px 12px', background: '#ffeaea', borderRadius: '6px' }}>
                  Please <Link to="/login" style={{ color: '#1F396C', fontWeight: 600 }}>login</Link> to use this feature.
                </div>
              )}

              {/* Professional Info */}
              <div className="professional-info">
                <div className="info-item">
                  <span className="info-label">Category:</span>
                  <span className="info-value">{professionalData.category}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Expertise:</span>
                  <span className="info-value">{professionalData.expertise}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Interested location:</span>
                  <span className="info-value">{professionalData.interestedLocations}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Experience:</span>
                  <span className="info-value">{professionalData.experience}</span>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="profile-tabs">
                <button className="tab-item active">
                  All Project ({professionalData.projectCount})
                </button>
              </div>
            </div>

            {/* Cover Image */}
            <div className="cover-image-container">
              <img 
                src={professionalData.coverImage} 
                alt="Cover" 
                className="cover-image"
              />
              <button 
                className="bookmark-btn"
                onClick={() => setIsBookmarked(!isBookmarked)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M17 3H6.5C6.10218 3 5.72064 3.15804 5.43934 3.43934C5.15804 3.72064 5 4.10218 5 4.5V21C5.00007 21.1338 5.03595 21.2652 5.10393 21.3805C5.17191 21.4958 5.2695 21.5908 5.38659 21.6557C5.50367 21.7206 5.63598 21.7529 5.76978 21.7494C5.90358 21.7458 6.034 21.7066 6.1475 21.6356L11.75 18.1341L17.3534 21.6356C17.4669 21.7063 17.5972 21.7454 17.7309 21.7488C17.8646 21.7522 17.9967 21.7198 18.1136 21.655C18.2306 21.5902 18.3281 21.4953 18.396 21.3801C18.4639 21.2649 18.4998 21.1337 18.5 21V4.5C18.5 4.10218 18.342 3.72064 18.0607 3.43934C17.7794 3.15804 17.3978 3 17 3ZM17 19.6472L12.1466 16.6144C12.0274 16.5399 11.8896 16.5004 11.7491 16.5004C11.6085 16.5004 11.4708 16.5399 11.3516 16.6144L6.5 19.6472V4.5H17V19.6472Z" 
                    fill={isBookmarked ? "#1F396C" : "#969696"}
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Projects Grid Section */}
          <div className="projects-section">
            <div className="projects-grid">
              {projects.map((project) => (
                <Link to={`/project/${project.id}`} key={project.id} className="project-card">
                  <div className="project-image-wrapper">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="project-image"
                    />
                  </div>
                  <div className="project-info">
                    <h3 className="project-type">{project.type}</h3>
                    <p className="project-title">{project.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Contact Specialist Modal */}
      {showContactModal && professionalData && (
        <div className="pp-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="pp-contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-contact-modal-header">
              <div className="pp-contact-modal-profile">
                <img src={professionalData.avatar} alt={professionalData.name} className="pp-contact-avatar" />
                <div>
                  <h4 className="pp-contact-name">{professionalData.name}</h4>
                  <p className="pp-contact-title">{professionalData.title}</p>
                </div>
              </div>
              <button className="pp-contact-close" onClick={() => setShowContactModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="pp-contact-modal-body">
              {contactSuccess ? (
                <div className="pp-contact-success">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                    <path d="M8 12l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h4>Message Sent!</h4>
                  <p>Your inquiry has been sent to {professionalData.name}. They will respond via email shortly.</p>
                  <button className="pp-contact-btn-primary" onClick={() => setShowContactModal(false)}>Done</button>
                </div>
              ) : (
                <>
                  <div className="pp-contact-info-row">
                    <div className="pp-contact-info-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.908.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="#666" strokeWidth="1.5"/></svg>
                      <span>{professionalData.phone || 'Not provided'}</span>
                    </div>
                    <div className="pp-contact-info-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="#666" strokeWidth="1.5"/><circle cx="12" cy="10" r="3" stroke="#666" strokeWidth="1.5"/></svg>
                      <span>{professionalData.location}</span>
                    </div>
                  </div>
                  <label className="pp-contact-label">Your Message</label>
                  <textarea
                    className="pp-contact-textarea"
                    placeholder={`Hi ${professionalData.name}, I'm interested in your services...`}
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    rows={4}
                  />
                  <button
                    className="pp-contact-btn-primary"
                    disabled={!contactMessage.trim() || contactSending}
                    onClick={async () => {
                      setContactSending(true);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${API_BASE_URL}/specialists/${professionalData.id}/contact`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token && { 'Authorization': `Bearer ${token}` })
                          },
                          body: JSON.stringify({ message: contactMessage })
                        });
                        if (res.ok) {
                          setContactSuccess(true);
                        } else {
                          const errData = await res.json().catch(() => ({}));
                          alert(errData.error || 'Failed to send message. Please try again.');
                        }
                      } catch (err) {
                        console.error('Contact error:', err);
                        alert('Network error. Please check your connection and try again.');
                      } finally {
                        setContactSending(false);
                      }
                    }}
                  >
                    {contactSending ? 'Sending...' : 'Send Inquiry'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalProfilePage;
